import React, { useEffect, useState } from 'react';
import { Filter, Download, Inbox, Loader2, RefreshCw, Search, X, ChevronDown, ChevronLeft, ChevronRight, Eye, CheckCircle2, Clock, FileText, Trash2 } from 'lucide-react';
import { submissionsApi, SubmissionRecord, newslettersApi, NewsletterRecord, CreatorFilter } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
type StatusFilter = 'all' | 'Not Subscribed' | 'Confirmed';
/** Parse Form_Payload JSON safely */
function parsePayload(raw: string): Record<string, any> | null {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
/** Extract email from payload (tries common field names) */
function extractEmail(payload: Record<string, any> | null): string {
  if (!payload) return '-';
  return payload.email || payload.Email || payload.EMAIL || payload.e_mail || '-';
}
/** Extract a display name from payload */
function extractName(payload: Record<string, any> | null): string {
  if (!payload) return '';
  const first = payload.firstName || payload.first_name || payload.FirstName || payload.name || '';
  const last = payload.lastName || payload.last_name || payload.LastName || '';
  return [first, last].filter(Boolean).join(' ');
}
const PAGE_SIZE = 200; // Zoho Creator default batch size
export function Submissions() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [submissions, setSubmissions] = useState<SubmissionRecord[]>([]);
  const [newsletters, setNewsletters] = useState<NewsletterRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionRecord | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // Server-side tab counts
  const [allCount, setAllCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const showPagination = totalCount > PAGE_SIZE;
  const paginationPanel = showPagination ? <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3" data-id="element-6348">
      <div className="text-sm text-gray-600" data-id="element-6349">
        Showing {(currentPage - 1) * PAGE_SIZE + 1}–
        {Math.min(currentPage * PAGE_SIZE, totalCount)} of{' '}
        {totalCount.toLocaleString()} total records
      </div>
      <div className="flex items-center gap-2" data-id="element-6350">
        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1 || loading} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6351">
          <ChevronLeft className="h-4 w-4" data-id="element-6352" />
          Previous
        </button>
        <span className="text-sm text-gray-700 px-2" data-id="element-6353">
          Page {currentPage} of {totalPages}
        </span>
        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages || loading} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6354">
          Next
          <ChevronRight className="h-4 w-4" data-id="element-6355" />
        </button>
      </div>
    </div> : null;
  const buildScopeFilters = (): CreatorFilter[] => {
    const filters: CreatorFilter[] = [];
    filters.push({
      field: 'Team',
      value: currentWorkspace.name
    });
    if (currentSubRegion) {
      filters.push({
        field: 'Region',
        value: currentSubRegion
      });
    }
    return filters;
  };
  const fetchData = async (page: number = 1, status: StatusFilter = statusFilter, search: string = activeSearch) => {
    setLoading(true);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const scopeFilters = buildScopeFilters();
      // Search filter (multi-field)
      const searchFilter: CreatorFilter[] = search.trim() ? [{
        fields: ['Subscriber_Email', 'search_text'],
        value: search.trim()
      }] : [];
      // Build data filters: scope + optional status + optional search
      const dataFilters: CreatorFilter[] = [...scopeFilters, ...searchFilter];
      if (status !== 'all') {
        dataFilters.push({
          field: 'Status',
          value: status
        });
      }
      // Count filters include scope + search (but not status)
      const countBaseFilters: CreatorFilter[] = [...scopeFilters, ...searchFilter];
      const [subsResult, nlResult, pendingResult, confirmedResult] = await Promise.all([submissionsApi.list({
        offset: offset > 0 ? offset : undefined,
        filters: dataFilters
      }), newslettersApi.list(), submissionsApi.list({
        filters: [...countBaseFilters, {
          field: 'Status',
          value: 'Not Subscribed'
        }],
        only_count: true
      }), submissionsApi.list({
        filters: [...countBaseFilters, {
          field: 'Status',
          value: 'Confirmed'
        }],
        only_count: true
      })]);
      setSubmissions(subsResult.data);
      setTotalCount(subsResult.total_count);
      setAllCount(pendingResult.total_count + confirmedResult.total_count);
      setPendingCount(pendingResult.total_count);
      setConfirmedCount(confirmedResult.total_count);
      setNewsletters(nlResult);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };
  const handleStatusFilterChange = (newStatus: StatusFilter) => {
    setStatusFilter(newStatus);
    fetchData(1, newStatus, activeSearch);
  };
  const handleSearch = () => {
    const trimmed = searchQuery.trim();
    setActiveSearch(trimmed);
    fetchData(1, statusFilter, trimmed);
  };
  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearch('');
    fetchData(1, statusFilter, '');
  };
  useEffect(() => {
    fetchData(1, statusFilter, activeSearch);
  }, [currentWorkspace.name, currentSubRegion]);
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    fetchData(page);
  };
  // Resolve newsletter name from Newsletter_ID
  const getNewsletterName = (newsletterId: string): string => {
    const nl = newsletters.find(n => n.Newsletter_ID === newsletterId);
    return nl?.Newsletter || newsletterId || '-';
  };
  const sortedSubmissions = submissions;
  const showConfirmedAt = statusFilter === 'Confirmed';
  // Tab counts are fetched server-side via only_count requests
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      await submissionsApi.delete(id);
      setSubmissions(prev => prev.filter(s => s.ID !== id));
      setDeleteConfirm(null);
      toast.success('Submission deleted');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to delete submission', {
        description: msg
      });
    } finally {
      setIsDeleting(false);
    }
  };
  return <div className="space-y-6" data-id="element-6356">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-6357">
        <div className="min-w-0" data-id="element-6358">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-6359">
            Form Submissions
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-6360">
            All form submissions from your signup forms.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0" data-id="element-6361">
          <button onClick={() => fetchData(currentPage)} disabled={loading} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" title="Refresh" data-id="element-6362">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-6363" />
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3" data-id="element-6364">
        {/* Status filter tabs */}
        <div className="flex bg-gray-100 rounded-lg p-0.5" data-id="element-6365">
          <button onClick={() => handleStatusFilterChange('all')} disabled={loading} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-6366">
            All ({allCount})
          </button>
          <button onClick={() => handleStatusFilterChange('Not Subscribed')} disabled={loading} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'Not Subscribed' ? 'bg-white text-yellow-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-6367">
            <span className="flex items-center gap-1.5" data-id="element-6368">
              <Clock className="h-3.5 w-3.5" data-id="element-6369" />
              Pending ({pendingCount})
            </span>
          </button>
          <button onClick={() => handleStatusFilterChange('Confirmed')} disabled={loading} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'Confirmed' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-6370">
            <span className="flex items-center gap-1.5" data-id="element-6371">
              <CheckCircle2 className="h-3.5 w-3.5" data-id="element-6372" />
              Confirmed ({confirmedCount})
            </span>
          </button>
        </div>

        {/* Search */}
        <div className="flex gap-2 flex-1 max-w-md" data-id="element-6373">
          <div className="relative flex-1" data-id="element-6374">
            <input type="text" placeholder="Search by email, name, or newsletter..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter') handleSearch();
          }} className="pl-3 pr-8 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-6375" />
            {searchQuery && <button onClick={handleClearSearch} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-6376">
                <X className="h-3.5 w-3.5" data-id="element-6377" />
              </button>}
          </div>
          <button onClick={handleSearch} disabled={loading} className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50" title="Search" data-id="element-6378">
            <Search className="h-4 w-4" data-id="element-6379" />
          </button>
        </div>
      </div>

      {/* Top Pagination */}
      {paginationPanel}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-6380">
        {loading && submissions.length === 0 ? <div className="p-12 text-center" data-id="element-6381">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-6382" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-6383">Loading submissions...</p>
          </div> : submissions.length === 0 ? <div className="p-12 text-center" data-id="element-6384">
            <Inbox className="mx-auto h-12 w-12 text-gray-400" data-id="element-6385" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-6386">
              {activeSearch || statusFilter !== 'all' ? 'No matching submissions' : 'No submissions yet'}
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-6387">
              {activeSearch || statusFilter !== 'all' ? 'Try adjusting your search or filter.' : 'Submissions will appear here when customers fill out your signup forms.'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-6388">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-6389">
              <thead className="bg-gray-50" data-id="element-6390">
                <tr data-id="element-6391">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6392">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6393">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6394">
                    Newsletter
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6395">
                    Status
                  </th>
                  {showConfirmedAt && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6396">
                      Confirmed At
                    </th>}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6397">
                    Region
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6398">
                    Payload
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-6399">
                {sortedSubmissions.map(sub => {
              const payload = parsePayload(sub.Form_Payload);
              const email = extractEmail(payload);
              const name = extractName(payload);
              return <tr key={sub.ID} className="hover:bg-gray-50" data-id="element-6400">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6401">
                        <span className="text-sm font-medium text-gray-900 truncate block max-w-[220px]" data-id="element-6402">
                          {email}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6403">
                        <span className="text-sm text-gray-600 truncate block max-w-[150px]" data-id="element-6404">
                          {name || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6405">
                        <span className="inline-flex items-center gap-1 text-sm text-gray-600 truncate max-w-[180px]" data-id="element-6406">
                          <FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" data-id="element-6407" />
                          {getNewsletterName(sub.Newsletter_ID)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6408">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${sub.Status === 'Confirmed' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-yellow-50 text-yellow-700 border border-yellow-100'}`} data-id="element-6409">
                          {sub.Status === 'Confirmed' ? <CheckCircle2 className="h-3 w-3" data-id="element-6410" /> : <Clock className="h-3 w-3" data-id="element-6411" />}
                          {sub.Status}
                        </span>
                      </td>
                      {showConfirmedAt && <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6412">
                          {sub.Confirmed_At1 ? new Date(sub.Confirmed_At1).toLocaleString() : '-'}
                        </td>}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6413">
                        {sub.Region || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-6414">
                        <div className="flex items-center justify-end gap-1" data-id="element-6415">
                          <button onClick={() => setSelectedSubmission(sub)} className="text-blue-600 hover:text-blue-900 text-sm inline-flex items-center gap-1 p-1.5 hover:bg-blue-50 rounded" data-id="element-6416">
                            <Eye className="h-3.5 w-3.5" data-id="element-6417" />
                            View
                          </button>
                          <button onClick={() => setDeleteConfirm(sub.ID)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete" data-id="element-6418">
                            <Trash2 className="h-3.5 w-3.5" data-id="element-6419" />
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Bottom Pagination */}
      {paginationPanel}

      {/* Payload Modal */}
      {selectedSubmission && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6420">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-6421">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedSubmission(null)} data-id="element-6422" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg" data-id="element-6423">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-6424">
                <div data-id="element-6425">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-6426">
                    Submission Payload
                  </h3>
                  <p className="text-xs text-gray-500 font-mono mt-0.5" data-id="element-6427">
                    ID: {selectedSubmission.ID}
                  </p>
                </div>
                <button onClick={() => setSelectedSubmission(null)} className="p-2 text-gray-400 hover:text-gray-600 rounded-md" data-id="element-6428">
                  <X className="h-5 w-5" data-id="element-6429" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-4" data-id="element-6430">
                {/* Status */}
                <div className="flex items-center justify-between" data-id="element-6431">
                  <span className="text-sm font-medium text-gray-700" data-id="element-6432">
                    Status
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium ${selectedSubmission.Status === 'Confirmed' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-yellow-50 text-yellow-700 border border-yellow-100'}`} data-id="element-6433">
                    {selectedSubmission.Status === 'Confirmed' ? <CheckCircle2 className="h-3 w-3" data-id="element-6434" /> : <Clock className="h-3 w-3" data-id="element-6435" />}
                    {selectedSubmission.Status}
                  </span>
                </div>

                {/* Newsletter */}
                <div className="flex items-center justify-between" data-id="element-6436">
                  <span className="text-sm font-medium text-gray-700" data-id="element-6437">
                    Newsletter
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-6438">
                    {getNewsletterName(selectedSubmission.Newsletter_ID)}
                  </span>
                </div>

                {/* Parsed fields */}
                {(() => {
              const payload = parsePayload(selectedSubmission.Form_Payload);
              if (!payload) return <div className="text-sm text-red-500" data-id="element-6439">
                        Could not parse payload
                      </div>;
              return <div className="space-y-2" data-id="element-6440">
                      <h4 className="text-sm font-medium text-gray-700" data-id="element-6441">
                        Form Fields
                      </h4>
                      <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-200" data-id="element-6442">
                        {Object.entries(payload).filter(([key]) => key !== 'newsletter_id' && key !== 'submitted_at').map(([key, value]) => <div key={key} className="flex items-center justify-between px-3 py-2" data-id="element-6443">
                              <span className="text-xs font-medium text-gray-500" data-id="element-6444">
                                {key}
                              </span>
                              <span className="text-sm text-gray-900 text-right max-w-[250px] truncate" data-id="element-6445">
                                {String(value)}
                              </span>
                            </div>)}
                      </div>

                      {payload.submitted_at && <div className="flex items-center justify-between pt-2" data-id="element-6446">
                          <span className="text-xs text-gray-400" data-id="element-6447">
                            Submitted at
                          </span>
                          <span className="text-xs text-gray-500" data-id="element-6448">
                            {new Date(payload.submitted_at).toLocaleString()}
                          </span>
                        </div>}
                    </div>;
            })()}

                {/* Raw JSON */}
                <details className="group" data-id="element-6449">
                  <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600" data-id="element-6450">
                    View raw JSON
                  </summary>
                  <pre className="mt-2 bg-gray-900 text-green-400 text-xs p-3 rounded-lg overflow-x-auto max-h-48" data-id="element-6451">
                    {JSON.stringify(parsePayload(selectedSubmission.Form_Payload), null, 2) || selectedSubmission.Form_Payload}
                  </pre>
                </details>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-6452">
                <button onClick={() => setSelectedSubmission(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-6453">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6454">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-6455">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-6456" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-6457">
              <div className="flex items-center gap-4 mb-4" data-id="element-6458">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-6459">
                  <Trash2 className="h-5 w-5 text-red-600" data-id="element-6460" />
                </div>
                <div data-id="element-6461">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-6462">
                    Delete Submission
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-6463">
                    Are you sure? This will permanently remove this submission
                    record.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3" data-id="element-6464">
                <button onClick={() => setDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-6465">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-6466">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-6467" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}